import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { format, Locale } from 'date-fns';

import {
    getEventIcon,
    getEventTypeColor,
    getMunduEventType,
    MunduEvent,
} from 'src/domain/event';
import { EventIconGraphic } from './EventIconGraphic';
import {
    getAllDayEvents,
    getTimeAxis,
    getWeekDays,
    layoutDayEvents,
    TimeAxis,
} from './residentWeekPlanLayout';

type ResidentWeekEvent = {
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay?: boolean;
    resource: MunduEvent;
};

type ResidentWeekPlanProps = {
    date: Date;
    events: ResidentWeekEvent[];
    locale: Locale;
};

const formatHour = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

const getRowStyle = (
    row: TimeAxis['rows'][number],
    axis: TimeAxis,
): CSSProperties => ({
    top: `${(row.offset / axis.totalUnits) * 100}%`,
    height: `${(row.size / axis.totalUnits) * 100}%`,
});

// hour lines, drawn behind the events of one column
const TimeAxisBands = ({ axis }: { axis: TimeAxis }) => (
    <>
        {axis.rows.map(row => (
            <span
                key={row.startHour}
                className="mundu-calendar-week-plan-band"
                style={getRowStyle(row, axis)}
                aria-hidden="true"
            />
        ))}
    </>
);

const getEventColorStyle = (event: ResidentWeekEvent) => {
    const color = getEventTypeColor(getMunduEventType(event.resource));

    return {
        '--mundu-event-color': color.background,
        '--mundu-event-text-color': color.foreground,
    } as CSSProperties;
};

const WeekPlanEventCard = ({
    event,
    timeLabel,
    style,
}: {
    event: ResidentWeekEvent;
    timeLabel: string;
    style: CSSProperties;
}) => {
    const eventIcon = getEventIcon(event.resource.icon);

    return (
        <article className="rbc-event" style={style}>
            <span className="mundu-calendar-week-plan-time">
                <span className="rbc-event-label">{timeLabel}</span>
                {eventIcon ? (
                    <EventIconGraphic
                        icon={eventIcon.key}
                        className="mundu-calendar-week-plan-icon"
                    />
                ) : null}
            </span>
            <span className="mundu-calendar-week-plan-title">
                {event.title}
            </span>
            {event.resource.location ? (
                <span className="mundu-calendar-week-plan-location">
                    {event.resource.location}
                </span>
            ) : null}
        </article>
    );
};

export const ResidentWeekPlan = ({
    date,
    events,
    locale,
}: ResidentWeekPlanProps) => {
    const { t } = useTranslation();
    const allDayLabel = t('modules.calendar.views.allDay');
    const days = getWeekDays(date);
    const axis = getTimeAxis(events, days);
    const allDayEventsByDay = days.map(day => getAllDayEvents(events, day));
    const hasAllDayEvents = allDayEventsByDay.some(
        dayEvents => dayEvents.length > 0,
    );
    const gridStyle = {
        '--mundu-week-plan-hours': axis.totalUnits,
    } as CSSProperties;

    return (
        <div className="mundu-calendar-week-plan-scroll">
            <div className="mundu-calendar-week-plan" style={gridStyle}>
                <div className="mundu-calendar-week-plan-row">
                    <span className="mundu-calendar-week-plan-gutter" />
                    {days.map(day => {
                        const dayKey = format(day, 'yyyy-MM-dd');

                        return (
                            <h3 className="rbc-header" key={dayKey}>
                                <time dateTime={dayKey}>
                                    {format(day, 'd EEE', { locale })}
                                </time>
                            </h3>
                        );
                    })}
                </div>
                {hasAllDayEvents ? (
                    <div className="mundu-calendar-week-plan-row mundu-calendar-week-plan-all-day">
                        <span className="mundu-calendar-week-plan-gutter">
                            {allDayLabel}
                        </span>
                        {days.map((day, dayIndex) => (
                            <div
                                className="mundu-calendar-week-plan-all-day-events"
                                key={format(day, 'yyyy-MM-dd')}
                            >
                                {allDayEventsByDay[dayIndex].map(event => (
                                    <WeekPlanEventCard
                                        key={event.id}
                                        event={event}
                                        timeLabel={allDayLabel}
                                        style={getEventColorStyle(event)}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                ) : null}
                <div className="mundu-calendar-week-plan-row mundu-calendar-week-plan-body">
                    <div className="mundu-calendar-week-plan-gutter">
                        <TimeAxisBands axis={axis} />
                        {axis.rows.map(row => (
                            <span
                                className="mundu-calendar-week-plan-hour"
                                key={row.startHour}
                                style={{
                                    top: `${
                                        (row.offset / axis.totalUnits) * 100
                                    }%`,
                                }}
                            >
                                {formatHour(row.startHour)}
                            </span>
                        ))}
                    </div>
                    {days.map(day => (
                        <div
                            className="mundu-calendar-week-plan-day"
                            key={format(day, 'yyyy-MM-dd')}
                        >
                            <TimeAxisBands axis={axis} />
                            <div className="mundu-calendar-week-plan-day-events">
                                {layoutDayEvents(events, day, axis).map(
                                    ({
                                        event,
                                        top,
                                        height,
                                        column,
                                        columns,
                                    }) => (
                                        <WeekPlanEventCard
                                            key={event.id}
                                            event={event}
                                            timeLabel={`${format(
                                                event.start,
                                                'HH:mm',
                                            )} – ${format(event.end, 'HH:mm')}`}
                                            style={{
                                                ...getEventColorStyle(event),
                                                top: `${top}%`,
                                                // the 2px trim keeps a gap between
                                                // back-to-back and side-by-side cards
                                                height: `calc(${height}% - 2px)`,
                                                left: `${
                                                    (column / columns) * 100
                                                }%`,
                                                width: `calc(${
                                                    100 / columns
                                                }% - 2px)`,
                                            }}
                                        />
                                    ),
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
